import React, { useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Plus, Trash } from "@phosphor-icons/react";
import { useQueryClient } from "react-query";
import {
  supplierAdministrationKeys,
  useGetSupplierLocations,
  useCreateSupplierLocation,
  useUpdateSupplierLocation,
  useDeleteSupplierLocation,
} from "../../../hooks/query/supplier-administration";
import { useGetCountries, useGetAllSupplierCategories } from "../../../hooks/query";
import { constructQueryParamsString, showErrorToast, showSuccessToast } from "../../../utils";
import LocalSpinner from "../../../components/LocalSpinner";

const LIME_FOCUS = {
  _hover: { borderColor: "gray.400" },
  _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  _focusVisible: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
};

const TIER_OPTIONS = [1, 2, 3, 4];

const EMPTY = { name: "", category: "", tier: "" };

// Inline location row — shows either saved data or an editable form
function LocationRow({ loc, supplierName, countries, onSaved, onDeleted }) {
  const toast = useToast();
  const [editing, setEditing] = useState(!loc.id);
  const [form, setForm] = useState({ location: loc.location ?? "", industryCode: loc.industryCode ?? "" });

  const { mutate: create, isLoading: creating } = useCreateSupplierLocation({
    onSuccess: (saved) => { showSuccessToast(toast, "Location added"); onSaved(saved); setEditing(false); },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });
  const { mutate: update, isLoading: updating } = useUpdateSupplierLocation({
    onSuccess: (saved) => { showSuccessToast(toast, "Location updated"); onSaved(saved); setEditing(false); },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });
  const { mutate: remove, isLoading: deleting } = useDeleteSupplierLocation({
    onSuccess: () => { showSuccessToast(toast, "Location removed"); onDeleted(loc.id); },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const isBusy = creating || updating || deleting;
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = () => {
    if (!form.location) return;
    const payload = { supplier: supplierName, location: form.location, industryCode: form.industryCode };
    if (loc.id) {
      update({ id: loc.id, data: payload });
    } else {
      create(payload);
    }
  };

  if (!editing && loc.id) {
    return (
      <Flex align="center" gap={3} py={2} px={3} bg="gray.50" borderRadius="md">
        <Text fontSize="13px" color="darkTeal" fontWeight="600" flex={1}>{loc.location}</Text>
        {loc.industryCode && (
          <Badge bg="purple.50" color="purple.700" borderRadius="full" px={2} py={0.5} fontSize="11px" fontWeight="600" textTransform="none">
            {loc.industryCode}
          </Badge>
        )}
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Edit location"
          icon={<Text fontSize="11px" fontWeight="700">Edit</Text>}
          onClick={() => setEditing(true)}
        />
        <IconButton
          size="xs"
          variant="ghost"
          aria-label="Delete location"
          colorScheme="red"
          icon={<Trash size={12} />}
          isLoading={deleting}
          onClick={() => remove(loc.id)}
        />
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" borderRadius="md" p={3} border="1px dashed" borderColor="gray.300">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mb={3}>
        <FormControl isRequired>
          <FormLabel fontSize="xs" mb={1}>Country</FormLabel>
          <Select
            size="sm"
            value={form.location}
            onChange={set("location")}
            placeholder="Select country"
            {...LIME_FOCUS}
          >
            {(countries ?? []).map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={1}>Industry code</FormLabel>
          <Input size="sm" value={form.industryCode} onChange={set("industryCode")} placeholder="e.g. C25.1" {...LIME_FOCUS} />
        </FormControl>
      </SimpleGrid>
      <HStack spacing={2} justify="flex-end">
        {loc.id && (
          <Button size="xs" variant="ghost" onClick={() => { setEditing(false); setForm({ location: loc.location ?? "", industryCode: loc.industryCode ?? "" }); }}>
            Cancel
          </Button>
        )}
        <Button size="xs" bg="limeShade.600" color="white" _hover={{ bg: "limeShade.700" }} isLoading={isBusy} isDisabled={!form.location} onClick={handleSave}>
          {loc.id ? "Update" : "Add location"}
        </Button>
      </HStack>
    </Box>
  );
}

// Supplier add/edit form modal — category from DB, tier selection, multiple locations, industry code per location.
function SupplierFormModal({ isOpen, onClose, onSave, supplier = null }) {
  const isEdit = !!supplier;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);

  const { data: categoriesData } = useGetAllSupplierCategories({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });
  const categoryOptions = (categoriesData ?? []).map((c) => ({ value: c.name, label: c.name, id: c.id }));
  // Local list of location rows (persisted separately via /suppliers/locations)
  const [locations, setLocations] = useState([]);
  // A sentinel "new row" for adding locations inline
  const [addingNew, setAddingNew] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(supplier
        ? { name: supplier.name ?? "", category: supplier.category ?? "", tier: supplier.tier ?? "" }
        : EMPTY);
      setAddingNew(false);
    }
  }, [isOpen, supplier]);

  // Fetch existing locations for this supplier when editing
  const locationsParamsString = supplier?.name
    ? constructQueryParamsString({ supplier: supplier.name, page: 0, size: 200 })
    : null;

  const { isFetching: loadingLocations } = useGetSupplierLocations(
    locationsParamsString ?? "",
    {
      enabled: isOpen && isEdit && !!supplier?.name,
      onSuccess: (data) => setLocations(data?.content ?? []),
      onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0,
    }
  );

  // Country list for location dropdowns
  const { data: countries, isFetching: loadingCountries } = useGetCountries({
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const isValid = form.name.trim() && form.category && form.tier;

  const handleSaved = (savedLoc) => {
    setLocations((prev) => {
      const exists = prev.find((l) => l.id === savedLoc.id);
      if (exists) return prev.map((l) => (l.id === savedLoc.id ? savedLoc : l));
      return [...prev, savedLoc];
    });
    setAddingNew(false);
    queryClient.invalidateQueries(supplierAdministrationKeys.supplierLocations());
  };

  const handleDeleted = (id) => {
    setLocations((prev) => prev.filter((l) => l.id !== id));
    queryClient.invalidateQueries(supplierAdministrationKeys.supplierLocations());
  };

  const handleSubmit = () => {
    if (!isValid) return;
    onSave(isEdit ? { ...supplier, ...form } : { ...form });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader color="darkTeal" fontWeight="700">{isEdit ? "Edit supplier" : "Add supplier"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Core supplier fields */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">Supplier name</FormLabel>
              <Input value={form.name} onChange={set("name")} placeholder="e.g. Helix Materials Ltd" {...LIME_FOCUS} />
            </FormControl>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Category</FormLabel>
                <Select value={form.category} onChange={set("category")} placeholder="Select category" {...LIME_FOCUS}>
                  {categoryOptions.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel fontSize="sm">Tier</FormLabel>
                <Select value={form.tier} onChange={set("tier")} placeholder="Select tier" {...LIME_FOCUS}>
                  {TIER_OPTIONS.map((t) => (
                    <option key={t} value={t}>Tier {t}</option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>

            {/* Locations section — shown for existing suppliers only */}
            {isEdit && (
              <>
                <Divider />
                <Box>
                  <Flex justify="space-between" align="center" mb={3}>
                    <Text fontSize="sm" fontWeight="700" color="darkTeal">Locations</Text>
                    {!addingNew && (
                      <Button
                        size="xs"
                        variant="outline"
                        leftIcon={<Plus size={12} weight="bold" />}
                        borderRadius="full"
                        onClick={() => setAddingNew(true)}
                      >
                        Add location
                      </Button>
                    )}
                  </Flex>

                  {loadingLocations || loadingCountries ? (
                    <Box py={3}><LocalSpinner /></Box>
                  ) : (
                    <VStack spacing={2} align="stretch">
                      {locations.map((loc) => (
                        <LocationRow
                          key={loc.id}
                          loc={loc}
                          supplierName={form.name}
                          countries={countries}
                          onSaved={handleSaved}
                          onDeleted={handleDeleted}
                        />
                      ))}
                      {locations.length === 0 && !addingNew && (
                        <Text fontSize="13px" color="gray.400" py={2}>No locations recorded.</Text>
                      )}
                      {addingNew && (
                        <LocationRow
                          loc={{ id: null, location: "", industryCode: "" }}
                          supplierName={form.name}
                          countries={countries}
                          onSaved={handleSaved}
                          onDeleted={() => setAddingNew(false)}
                        />
                      )}
                    </VStack>
                  )}
                </Box>
              </>
            )}

            {!isEdit && (
              <Text fontSize="12px" color="gray.500">
                You can add locations once the supplier has been created.
              </Text>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="secondary" borderRadius="full" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg="limeShade.600"
            color="white"
            borderRadius="full"
            _hover={{ bg: "limeShade.700" }}
            size="sm"
            isDisabled={!isValid}
            onClick={handleSubmit}
          >
            {isEdit ? "Save changes" : "Add supplier"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default SupplierFormModal;
